import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import intl from "../../i18n/intl";
import { resetAll } from "./baseSlice";

const customIntl = intl();

// API error codes and the message shown for each. The API's 400 body is
// { response: "<code>", values: { message: "<English text>" }, status, code,
// error, success } (CustomResponseMiddleware rewrites every 400 to this shape).
const ERROR_MESSAGES = {
  SAVED_REPORT_LIMIT: "saved_report_limit_reached",
  DUPLICATE_NAME: "saved_report_duplicate_name",
  INVALID_SCHEDULE: "schedule_invalid",
  NO_REPORT_ACCESS: "saved_report_no_access",
  INVALID_SAVED_REPORT: "saved_report_invalid",
  SEND_NOW_IN_PROGRESS: "saved_report_send_now_in_progress",
  // Not an API code: the saga gives a 404 (no body code) this one.
  SAVED_REPORT_NOT_FOUND: "saved_report_not_found",
};

// Codes whose server message names the exact problem (the schedule validator's
// reason), so it is shown instead of the general text when the API sends one.
const SERVER_MESSAGE_CODES = ["INVALID_SCHEDULE"];

const showError = (error, fallback = "something_else_wrong") => {
  const code = error?.response;
  const serverMessage = error?.values?.message;
  if (serverMessage && SERVER_MESSAGE_CODES.includes(code)) {
    toast.error(serverMessage);
    return;
  }
  toast.error(
    customIntl.formatMessage({
      id: ERROR_MESSAGES[code] || fallback,
    })
  );
};

const withoutId = (ids, id) => (ids || []).filter((item) => item !== id);

const showSuccess = (id) => {
  toast.success(customIntl.formatMessage({ id }));
};

const initialState = {
  list: [],
  record: null,
  history: [],
  // Saved report ids whose Send now request has not been answered yet.
  sendingNow: [],
  // Each saved report list's search, by kind (Reports and My team).
  searches: {},
  requesting: false,
  action: null,
  error: null,
};

const start = (action) => (state) => {
  state.requesting = true;
  state.action = action;
  state.error = null;
};

const fail = (fallback) => (state, action) => {
  state.requesting = false;
  state.error = action.payload;
  showError(action.payload, fallback);
};

const succeed = (message) => (state) => {
  state.requesting = false;
  showSuccess(message);
};

const savedReportSlice = createSlice({
  name: "savedReports",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    savedReportListRequest: (state) => {
      start("list")(state);
      // The store is kept in local storage: a pending flag restored after a
      // reload would disable Send now for good. The API refuses a duplicate.
      state.sendingNow = [];
    },
    savedReportListSuccess: (state, action) => {
      state.requesting = false;
      state.list = action.payload?.response || [];
    },
    savedReportListFail: (state, action) => {
      fail()(state, action);
      state.list = [];
    },
    // payload { kind, namePattern }. A store restored from before searches
    // were kept has none.
    savedReportSetSearch: (state, action) => {
      state.searches = {
        ...(state.searches || {}),
        [action.payload.kind]: action.payload.namePattern,
      };
    },

    savedReportDetailsRequest: start("details"),
    savedReportDetailsSuccess: (state, action) => {
      state.requesting = false;
      state.record = action.payload?.response || null;
    },
    savedReportDetailsFail: (state, action) => {
      fail("saved_report_not_found")(state, action);
      state.record = null;
    },

    savedReportCreateRequest: start("create"),
    savedReportCreateSuccess: (state, action) => {
      const record = action.payload?.response;
      state.record = record;
      state.list = [...state.list, record];
      succeed("saved_report_saved")(state);
    },
    savedReportCreateFail: fail(),

    savedReportUpdateRequest: start("update"),
    savedReportUpdateSuccess: (state, action) => {
      const record = action.payload?.response;
      state.record = record;
      state.list = state.list.map((item) =>
        item.id === record?.id ? record : item
      );
      succeed("saved_report_updated")(state);
    },
    // payload: the error body with the saved report's id. A report deleted
    // since it was opened (for example in another tab) is closed: the
    // screen stops showing it as open, so Save asks for a name and creates
    // one with the current filters (B-BUG-5).
    savedReportUpdateFail: (state, action) => {
      fail()(state, action);
      if (action.payload?.response === "SAVED_REPORT_NOT_FOUND") {
        state.list = state.list.filter((item) => item.id !== action.payload.id);
        if (state.record?.id === action.payload.id) {
          state.record = null;
        }
      }
    },

    savedReportDeleteRequest: start("delete"),
    savedReportDeleteSuccess: (state, action) => {
      state.list = state.list.filter((item) => item.id !== action.payload.id);
      if (state.record?.id === action.payload.id) {
        state.record = null;
      }
      succeed("saved_report_deleted")(state);
    },
    savedReportDeleteFail: fail(),

    savedReportScheduleSaveRequest: start("schedule"),
    savedReportScheduleSaveSuccess: succeed("schedule_saved"),
    savedReportScheduleSaveFail: fail(),

    savedReportSchedulePauseRequest: start("pause"),
    savedReportSchedulePauseSuccess: succeed("schedule_paused"),
    savedReportSchedulePauseFail: fail(),

    savedReportScheduleResumeRequest: start("resume"),
    savedReportScheduleResumeSuccess: succeed("schedule_resumed"),
    savedReportScheduleResumeFail: fail(),

    savedReportScheduleDeleteRequest: start("unschedule"),
    savedReportScheduleDeleteSuccess: succeed("schedule_deleted"),
    savedReportScheduleDeleteFail: fail(),

    // payload { id }; the fail payload is { id, error }.
    savedReportSendNowRequest: (state, action) => {
      start("sendNow")(state);
      state.sendingNow = [
        ...withoutId(state.sendingNow, action.payload?.id),
        action.payload?.id,
      ];
    },
    savedReportSendNowSuccess: (state, action) => {
      succeed("saved_report_send_now_queued")(state);
      state.sendingNow = withoutId(state.sendingNow, action.payload?.id);
    },
    savedReportSendNowFail: (state, action) => {
      fail()(state, { payload: action.payload?.error });
      state.sendingNow = withoutId(state.sendingNow, action.payload?.id);
    },

    savedReportHistoryRequest: (state) => {
      start("history")(state);
      state.history = null;
    },
    savedReportHistorySuccess: (state, action) => {
      state.requesting = false;
      state.history = action.payload?.response || [];
    },
    savedReportHistoryFail: (state, action) => {
      fail()(state, action);
      state.history = [];
    },
  },
});

export const {
  savedReportListRequest,
  savedReportListSuccess,
  savedReportListFail,
  savedReportSetSearch,
  savedReportDetailsRequest,
  savedReportDetailsSuccess,
  savedReportDetailsFail,
  savedReportCreateRequest,
  savedReportCreateSuccess,
  savedReportCreateFail,
  savedReportUpdateRequest,
  savedReportUpdateSuccess,
  savedReportUpdateFail,
  savedReportDeleteRequest,
  savedReportDeleteSuccess,
  savedReportDeleteFail,
  savedReportScheduleSaveRequest,
  savedReportScheduleSaveSuccess,
  savedReportScheduleSaveFail,
  savedReportSchedulePauseRequest,
  savedReportSchedulePauseSuccess,
  savedReportSchedulePauseFail,
  savedReportScheduleResumeRequest,
  savedReportScheduleResumeSuccess,
  savedReportScheduleResumeFail,
  savedReportScheduleDeleteRequest,
  savedReportScheduleDeleteSuccess,
  savedReportScheduleDeleteFail,
  savedReportSendNowRequest,
  savedReportSendNowSuccess,
  savedReportSendNowFail,
  savedReportHistoryRequest,
  savedReportHistorySuccess,
  savedReportHistoryFail,
} = savedReportSlice.actions;
export default savedReportSlice.reducer;
